import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DnaBackground } from "@/components/DnaBackground";
import { Navigation } from "@/components/Navigation";
import ThemeLeaderboardList from "@/components/ThemeLeaderboardList";
import { getUserData, themesAPI, ThemeLibraryItem } from "@/lib/api";
import { translateDomainName, translateStrengthName } from "@/lib/strength-i18n";
import { STRENGTHS_EXPLORER_DOMAIN_COLORS } from "@/utils/constants/strengths";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

const getThemeDisplayName = (
  theme: { name: string; nameAr?: string | null },
  t: ReturnType<typeof useTranslation>["t"],
  language: string
) => {
  const englishName = theme.name;
  const arabicName = theme.nameAr || null;

  if (language?.split("-")[0] === "ar") {
    return {
      primary: arabicName || englishName,
      secondary: arabicName ? englishName : null,
    };
  }

  return {
    primary: englishName,
    secondary: arabicName ? arabicName : null,
  };
};

export default function ThemeDetail() {
  const { themeId } = useParams();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user] = useState(() => getUserData());
  const [theme, setTheme] = useState<ThemeLibraryItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    if (!themeId) {
      navigate("/strengths-explorer");
      return;
    }

    const loadTheme = async () => {
      setIsLoading(true);
      try {
        const response = await themesAPI.getThemes();
        const found = (response.data || []).find((item) => item.id === themeId);
        if (!found) {
          navigate("/strengths-explorer");
          return;
        }
        setTheme(found);
      } catch (error: any) {
        toast({
          title: t("strengthsExplorer.themesLoadFailed"),
          description: getLocalizedErrorMessage(error, "strengthsExplorer.themesLoadFailedDesc"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, [navigate, t, toast, themeId, user]);

  const color = theme ? STRENGTHS_EXPLORER_DOMAIN_COLORS[theme.domain] : undefined;

  return (
    <div className="min-h-screen overflow-x-hidden" dir={isRtl ? "rtl" : "ltr"}>
      <DnaBackground />
      <Navigation user={user} />

      <main className="pt-24 px-4 sm:px-6 pb-12">
        <div className="container mx-auto max-w-6xl min-w-0 space-y-6">
          <Button variant="ghost" className="gap-2 px-0" asChild>
            <Link to="/strengths-explorer" dir={isRtl ? "rtl" : "ltr"}>
              <ArrowLeft className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} />
              {t("strengthsExplorer.backToLibrary")}
            </Link>
          </Button>

          {isLoading || !theme ? (
            <Card className="flex items-center justify-center gap-3 p-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t("strengthsExplorer.loadingThemes")}
            </Card>
          ) : (
            <>
              <Card className="border-l-4" style={{ borderInlineStartColor: color, borderInlineStartWidth: 4 }}>
                <CardHeader>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <CardTitle className="text-2xl sm:text-3xl leading-tight">
                        {getThemeDisplayName(theme, t, i18n.language).primary}
                      </CardTitle>
                      {getThemeDisplayName(theme, t, i18n.language).secondary && (
                        <span className="text-xs font-medium opacity-75 tracking-wide" dir="ltr">
                          {getThemeDisplayName(theme, t, i18n.language).secondary}
                        </span>
                      )}
                    </div>
                    <Badge
                      className="border"
                      style={{ backgroundColor: `${color}15`, borderColor: `${color}40`, color }}
                    >
                      {translateDomainName(theme.domain, t, i18n.language)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {isRtl ? (theme.descriptionAr || theme.description) : theme.description}
                  </p>
                </CardContent>
              </Card>

              <div className="space-y-3">
                <h2 className="text-xl font-bold">{t("strengthsExplorer.leaderboardTitle")}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("strengthsExplorer.leaderboardSubtitle")}
                </p>
                <ThemeLeaderboardList themeId={theme.id} />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
